import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, ShieldCheck, Sparkles, Wallet, Plus, Save, KeyRound, Lock, Eye, EyeOff, Ban, ShieldOff, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

export default function AdminClientDetail() {
  const { t } = useLanguage();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [bal, setBal] = useState({ montant: '', motif: '', type: 'credit' });
  const [tx, setTx] = useState({ type: 'remboursement', libelle: '', montant: '', date_transaction: '' });
  const [genCard, setGenCard] = useState({ type_carte: 'visa', niveau_carte: 'basic' });
  const [showCard, setShowCard] = useState(false);
  const [showTx, setShowTx] = useState(false);

  // Sécurité : gestion du mot de passe
  const [showViewPwd, setShowViewPwd] = useState(false);
  const [pwdInfo, setPwdInfo] = useState(null); // {hashed, message, password}
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showRevealed, setShowRevealed] = useState(false);
  const [showEditPwd, setShowEditPwd] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);

  // Account blocking
  const [showBlock, setShowBlock] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [unblockSaving, setUnblockSaving] = useState(false);

  // Absolute balance adjustment (Feature v4)
  const [showAdjustBal, setShowAdjustBal] = useState(false);
  const [adjustBal, setAdjustBal] = useState({ balance: '', motif: '' });
  const [adjustBalSaving, setAdjustBalSaving] = useState(false);

  const load = async () => {
    const r = await api.get(`/admin/clients/${id}`);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (k, v) => {
    await api.post(`/admin/clients/${id}/status`, { [k]: v });
    toast.success('Statut mis à jour'); load();
  };
  const genActivation = async () => {
    const r = await api.post(`/admin/clients/${id}/activation-code`, { send_email: true });
    toast.success(`Code: ${r.data.code_activation} (envoyé par email)`);
    load();
  };
  const genIban = async () => {
    await api.post(`/admin/clients/${id}/iban`); toast.success('IBAN généré'); load();
  };
  const updateBal = async () => {
    if (!bal.montant || !bal.motif) { toast.error('Complétez les champs'); return; }
    await api.post(`/admin/clients/${id}/balance`, { ...bal, montant: parseFloat(bal.montant) });
    toast.success('Solde mis à jour'); setBal({ montant: '', motif: '', type: 'credit' }); load();
  };
  const createCard = async () => {
    await api.post(`/admin/clients/${id}/card`, genCard);
    toast.success('Carte générée'); setShowCard(false); load();
  };
  const createUpgradeCode = async (t) => {
    const r = await api.post(`/admin/clients/${id}/upgrade-code`, { type_upgrade: t });
    toast.success(`Code: ${r.data.code}`); load();
  };
  const addTx = async () => {
    if (!tx.libelle || !tx.montant) { toast.error('Complétez les champs'); return; }
    await api.post(`/admin/clients/${id}/transactions`, { ...tx, montant: parseFloat(tx.montant) });
    toast.success('Transaction ajoutée'); setShowTx(false); setTx({ type: 'remboursement', libelle: '', montant: '', date_transaction: '' }); load();
  };
  const doBlock = async () => {
    if (!blockReason.trim()) { toast.error('Le motif est obligatoire'); return; }
    try {
      setBlockSaving(true);
      await api.post(`/admin/clients/${id}/block`, { reason: blockReason.trim() });
      toast.success('Compte bloqué');
      setShowBlock(false);
      setBlockReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du blocage');
    } finally {
      setBlockSaving(false);
    }
  };

  const doUnblock = async () => {
    try {
      setUnblockSaving(true);
      await api.post(`/admin/clients/${id}/unblock`);
      toast.success('Compte débloqué');
      setShowUnblockConfirm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du déblocage');
    } finally {
      setUnblockSaving(false);
    }
  };

  // Absolute balance adjustment — PUT /admin/clients/{id}/balance (Feature v4)
  const openAdjustBal = () => {
    setAdjustBal({ balance: String(data?.user?.solde ?? 0), motif: '' });
    setShowAdjustBal(true);
  };
  const doAdjustBal = async () => {
    const bal = parseFloat(adjustBal.balance);
    if (Number.isNaN(bal)) { toast.error(t('transfer.invalidAmountErr')); return; }
    if (!adjustBal.motif.trim()) { toast.error('Le motif est obligatoire'); return; }
    try {
      setAdjustBalSaving(true);
      await api.put(`/admin/clients/${id}/balance`, {
        balance: bal,
        motif: adjustBal.motif.trim(),
      });
      toast.success(t('admin.balanceAdjusted'));
      setShowAdjustBal(false);
      setAdjustBal({ balance: '', motif: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setAdjustBalSaving(false);
    }
  };

  const openViewPwd = async () => {
    setShowViewPwd(true);
    setShowRevealed(false);
    setPwdInfo(null);
    try {
      setPwdLoading(true);
      const r = await api.get(`/admin/clients/${id}/password`);
      setPwdInfo(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de récupération');
      setShowViewPwd(false);
    } finally {
      setPwdLoading(false);
    }
  };

  const submitNewPwd = async () => {
    if (!newPwd || newPwd.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      setPwdSaving(true);
      await api.put(`/admin/clients/${id}/password`, { new_password: newPwd });
      toast.success('Mot de passe modifié avec succès');
      setShowEditPwd(false);
      setNewPwd('');
      setConfirmPwd('');
      setShowNewPwd(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setPwdSaving(false);
    }
  };

  // Édition du profil
  const [profileEdit, setProfileEdit] = useState(null);
  const openProfileEdit = () => {
    setProfileEdit({
      civilite: u.civilite || 'M.',
      nom: u.nom || '', prenom: u.prenom || '',
      telephone: u.telephone || '',
      adresse_numero: u.adresse_numero || '',
      adresse_rue: u.adresse_rue || '',
      adresse_code_postal: u.adresse_code_postal || '',
      adresse_ville: u.adresse_ville || '',
      adresse_pays: u.adresse_pays || 'France',
    });
  };
  const saveProfile = async () => {
    try {
      await api.put(`/admin/clients/${id}`, profileEdit);
      toast.success('Profil mis à jour');
      setProfileEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (!data) return <div>{t('common.loading')}</div>;
  const u = data.user;
  const card = data.card;
  const isBlocked = u.status === 'blocked' || u.statut_compte === 'bloque' || !!u.block_reason || !!u.bloque_jusqu_a;
  return (
    <div className="space-y-6">
      <Link to="/admin/clients" className="text-sm text-blue-700 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> {t('common.back')}</Link>

      {/* Status banner */}
      {isBlocked ? (
        <div className="rounded-2xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-4 flex items-start gap-3" data-testid="banner-account-blocked">
          <AlertTriangle className="w-6 h-6 text-rose-600 dark:text-rose-400 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-semibold text-rose-800 dark:text-rose-200">Compte bloqué</div>
            <div className="text-sm text-rose-700 dark:text-rose-300 mt-1">
              {u.block_reason ? <>Motif : <strong>{u.block_reason}</strong></> : 'Aucun motif renseigné.'}
              {u.blocked_at && (
                <span> · le {new Date(u.blocked_at).toLocaleString('fr-FR')}</span>
              )}
              {u.blocked_by && <span> par {u.blocked_by}</span>}
            </div>
          </div>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => setShowUnblockConfirm(true)}
            data-testid="btn-unblock-account"
          >
            <ShieldOff className="w-4 h-4 mr-1"/> Débloquer
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3 flex items-center gap-2 text-sm" data-testid="banner-account-active">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400"/>
          <span className="text-emerald-700 dark:text-emerald-300 font-medium">Compte actif</span>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{u.prenom} {u.nom}</h1>
          <p className="text-slate-500 text-sm">{u.email} • {u.telephone || '—'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge>{u.statut_compte}</Badge>
          <Badge className="capitalize">{u.statut_client || 'standard'}</Badge>
          <Button size="sm" variant="outline" onClick={openProfileEdit} data-testid="btn-edit-profile">
            <Save className="w-4 h-4 mr-1"/> Modifier le profil
          </Button>
          {isBlocked ? (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setShowUnblockConfirm(true)}
              data-testid="btn-unblock-account-header"
            >
              <ShieldOff className="w-4 h-4 mr-1"/> Débloquer
            </Button>
          ) : (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setShowBlock(true)}
              data-testid="btn-block-account"
            >
              <Ban className="w-4 h-4 mr-1"/> Bloquer le compte
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Solde */}
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold flex items-center gap-2"><Wallet className="w-5 h-5"/> Solde</h2>
          <div className="text-xs uppercase text-slate-500 tracking-wide mt-2">{t('admin.currentBalance')}</div>
          <div className="text-3xl font-bold">{(u.solde || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
          <Button
            onClick={openAdjustBal}
            variant="outline"
            className="w-full mt-3"
            data-testid="btn-open-adjust-balance"
          >
            <Wallet className="w-4 h-4 mr-1"/> {t('admin.adjustBalance')}
          </Button>
          <div className="mt-4 pt-4 border-t space-y-2">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Mouvement rapide (créditer/débiter)</p>
            <Select value={bal.type} onValueChange={(v) => setBal({ ...bal, type: v })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Créditer (+)</SelectItem>
                <SelectItem value="debit">Débiter (-)</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Montant" type="number" value={bal.montant} onChange={(e) => setBal({ ...bal, montant: e.target.value })} data-testid="input-balance-amount"/>
            <Input placeholder="Motif" value={bal.motif} onChange={(e) => setBal({ ...bal, motif: e.target.value })} data-testid="input-balance-motif"/>
            <Button onClick={updateBal} className="w-full bg-blue-700" data-testid="btn-update-balance">Mettre à jour</Button>
          </div>
        </div>

        {/* Statuts */}
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold">Statuts</h2>
          <div className="mt-4 space-y-3">
            <div><Label>Statut compte</Label>
              <Select value={u.statut_compte} onValueChange={(v) => updateStatus('statut_compte', v)}>
                <SelectTrigger data-testid="select-statut-compte"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="inactif">Inactif</SelectItem>
                  <SelectItem value="bloque">Bloqué</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Statut client</Label>
              <Select value={u.statut_client || 'standard'} onValueChange={(v) => updateStatus('statut_client', v)}>
                <SelectTrigger data-testid="select-statut-client"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="diamond">Diamond</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Activation / IBAN */}
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold">Activation & IBAN</h2>
          <div className="mt-3 text-sm">Code activation : <span className="font-mono">{u.code_activation || '—'}</span></div>
          <Button size="sm" onClick={genActivation} className="mt-2" data-testid="btn-gen-activation"><RefreshCw className="w-4 h-4 mr-1"/> Nouveau code & email</Button>
          <div className="mt-4 text-sm">IBAN : <span className="font-mono text-xs">{u.iban || '—'}</span></div>
          <div className="text-sm">BIC : <span className="font-mono">{u.bic || '—'}</span></div>
          <Button size="sm" onClick={genIban} className="mt-2 bg-blue-700" data-testid="btn-gen-iban"><ShieldCheck className="w-4 h-4 mr-1"/> Générer IBAN</Button>
        </div>
      </div>

      {/* Carte */}
      <div className="bg-white rounded-2xl border p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Carte bancaire</h2>
          <Button onClick={() => setShowCard(true)} className="bg-blue-700" data-testid="btn-create-card"><Sparkles className="w-4 h-4 mr-1"/> {card ? 'Régénérer' : 'Générer'} une carte</Button>
        </div>
        {card ? (
          <div className="mt-4 grid md:grid-cols-2 gap-5">
            <div className={`bfi-card ${card.niveau_carte || 'basic'}`}>
              <div className="flex items-center justify-between">
                <div><div className="text-xs uppercase tracking-widest opacity-80">BFI Groupe</div><div className="text-lg font-semibold capitalize">Carte {card.niveau_carte}</div></div>
                <div className="text-xl font-bold italic">{card.type_carte === 'visa' ? 'VISA' : 'MASTERCARD'}</div>
              </div>
              <div className="mt-10 font-mono tracking-widest text-lg">{card.numero_carte.replace(/(.{4})/g, '$1 ').trim()}</div>
              <div className="flex justify-between mt-3 text-xs"><span>{card.titulaire}</span><span>{card.date_expiration}</span><span>CVV {card.cvv}</span></div>
            </div>
            <div className="text-sm space-y-2">
              <div>Niveau : <Badge className="capitalize">{card.niveau_carte}</Badge></div>
              <div>Statut : <Badge>{card.statut}</Badge></div>
              <div className="font-semibold mt-3">Codes d'upgrade</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => createUpgradeCode('premium')} data-testid="btn-gen-upgrade-premium">Générer Premium</Button>
                <Button size="sm" variant="outline" onClick={() => createUpgradeCode('elite')} data-testid="btn-gen-upgrade-elite">Générer Elite</Button>
              </div>
              <div className="mt-2 space-y-1">
                {data.upgrade_codes?.map((c) => (
                  <div key={c.id} className="text-xs flex justify-between border rounded p-2 bg-slate-50">
                    <span className="font-mono">{c.code} • {c.type_upgrade}</span>
                    <span>{c.utilise ? 'Utilisé' : 'Disponible'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : <div className="text-slate-500 text-sm mt-4">Aucune carte générée</div>}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-2xl border p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Transactions</h2>
          <Button onClick={() => setShowTx(true)} className="bg-blue-700" data-testid="btn-add-transaction"><Plus className="w-4 h-4 mr-1"/> Ajouter</Button>
        </div>
      </div>

      {/* Sécurité — gestion du mot de passe */}
      <div className="bg-white rounded-2xl border p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold flex items-center gap-2"><Lock className="w-5 h-5"/> Sécurité</h2>
            <p className="text-sm text-slate-500 mt-1">Gérez le mot de passe du compte de ce client.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={openViewPwd} data-testid="btn-view-password">
              <Eye className="w-4 h-4 mr-1"/> Voir le mot de passe
            </Button>
            <Button className="bg-blue-700" onClick={() => setShowEditPwd(true)} data-testid="btn-edit-password">
              <KeyRound className="w-4 h-4 mr-1"/> Modifier le mot de passe
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showCard} onOpenChange={setShowCard}>
        <DialogContent>
          <DialogHeader><DialogTitle>Générer une carte</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={genCard.type_carte} onValueChange={(v) => setGenCard({ ...genCard, type_carte: v })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="visa">Visa</SelectItem>
                <SelectItem value="mastercard">Mastercard</SelectItem>
              </SelectContent>
            </Select>
            <Select value={genCard.niveau_carte} onValueChange={(v) => setGenCard({ ...genCard, niveau_carte: v })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCard(false)}>Annuler</Button>
            <Button onClick={createCard} className="bg-blue-700" data-testid="btn-create-card-submit"><Save className="w-4 h-4 mr-1"/> Générer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTx} onOpenChange={setShowTx}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle transaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={tx.type} onValueChange={(v) => setTx({ ...tx, type: v })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="virement_recu">Virement reçu</SelectItem>
                <SelectItem value="virement_emis">Virement émis</SelectItem>
                <SelectItem value="paiement_carte">Paiement carte</SelectItem>
                <SelectItem value="frais">Frais</SelectItem>
                <SelectItem value="remboursement">Remboursement</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Libellé" value={tx.libelle} onChange={(e) => setTx({ ...tx, libelle: e.target.value })}/>
            <Input placeholder="Montant (positif ou négatif)" type="number" step="0.01" value={tx.montant} onChange={(e) => setTx({ ...tx, montant: e.target.value })}/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTx(false)}>Annuler</Button>
            <Button onClick={addTx} className="bg-blue-700" data-testid="btn-add-tx-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Édition du profil client */}
      <Dialog open={!!profileEdit} onOpenChange={(o) => !o && setProfileEdit(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Modifier le profil de {u.prenom} {u.nom}</DialogTitle></DialogHeader>
          {profileEdit && (
            <div className="grid md:grid-cols-2 gap-3 py-3 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label>Civilité</Label>
                <Select value={profileEdit.civilite} onValueChange={(v) => setProfileEdit({ ...profileEdit, civilite: v })}>
                  <SelectTrigger data-testid="edit-profile-civilite"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M.">M.</SelectItem>
                    <SelectItem value="Mme">Mme</SelectItem>
                    <SelectItem value="Mx">Mx</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Téléphone</Label><Input value={profileEdit.telephone} onChange={(e) => setProfileEdit({ ...profileEdit, telephone: e.target.value })} data-testid="edit-profile-telephone"/></div>
              <div><Label>Prénom</Label><Input value={profileEdit.prenom} onChange={(e) => setProfileEdit({ ...profileEdit, prenom: e.target.value })} data-testid="edit-profile-prenom"/></div>
              <div><Label>Nom</Label><Input value={profileEdit.nom} onChange={(e) => setProfileEdit({ ...profileEdit, nom: e.target.value })} data-testid="edit-profile-nom"/></div>
              <div><Label>N° de rue</Label><Input value={profileEdit.adresse_numero} onChange={(e) => setProfileEdit({ ...profileEdit, adresse_numero: e.target.value })} data-testid="edit-profile-num"/></div>
              <div><Label>Rue</Label><Input value={profileEdit.adresse_rue} onChange={(e) => setProfileEdit({ ...profileEdit, adresse_rue: e.target.value })} data-testid="edit-profile-rue"/></div>
              <div><Label>Code postal</Label><Input value={profileEdit.adresse_code_postal} onChange={(e) => setProfileEdit({ ...profileEdit, adresse_code_postal: e.target.value })} data-testid="edit-profile-cp"/></div>
              <div><Label>Ville</Label><Input value={profileEdit.adresse_ville} onChange={(e) => setProfileEdit({ ...profileEdit, adresse_ville: e.target.value })} data-testid="edit-profile-ville"/></div>
              <div className="md:col-span-2"><Label>Pays</Label><Input value={profileEdit.adresse_pays} onChange={(e) => setProfileEdit({ ...profileEdit, adresse_pays: e.target.value })} data-testid="edit-profile-pays"/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileEdit(null)}>Annuler</Button>
            <Button onClick={saveProfile} className="bg-blue-700" data-testid="btn-save-profile">
              <Save className="w-4 h-4 mr-1"/> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: voir le mot de passe */}
      <Dialog open={showViewPwd} onOpenChange={setShowViewPwd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-700"/> Mot de passe du client
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm">
            {pwdLoading && (
              <div className="flex items-center gap-2 text-slate-500">
                <RefreshCw className="w-4 h-4 animate-spin"/> Chargement…
              </div>
            )}
            {!pwdLoading && pwdInfo?.hashed && (
              <div className="space-y-2">
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5"/> Chiffré (bcrypt)
                </div>
                <p className="text-slate-700">{pwdInfo.message}</p>
                <p className="text-slate-500 text-xs">
                  Pour des raisons de sécurité, BFI Groupe ne conserve pas le mot de passe en clair.
                  Cliquez sur « Modifier le mot de passe » pour en générer un nouveau et le communiquer
                  au client par un canal sécurisé.
                </p>
              </div>
            )}
            {!pwdLoading && pwdInfo && !pwdInfo.hashed && (
              <div className="space-y-3">
                <Label>Mot de passe enregistré</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={pwdInfo.password || ''}
                    type={showRevealed ? 'text' : 'password'}
                    className="font-mono"
                    data-testid="input-password-revealed"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowRevealed((v) => !v)}>
                    {showRevealed ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </Button>
                </div>
                {!pwdInfo.password && (
                  <p className="text-slate-500 text-xs">Aucun mot de passe enregistré pour ce client.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewPwd(false)}>Fermer</Button>
            <Button
              className="bg-blue-700"
              onClick={() => { setShowViewPwd(false); setShowEditPwd(true); }}
            >
              <KeyRound className="w-4 h-4 mr-1"/> Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: modifier le mot de passe */}
      <Dialog
        open={showEditPwd}
        onOpenChange={(o) => {
          setShowEditPwd(o);
          if (!o) { setNewPwd(''); setConfirmPwd(''); setShowNewPwd(false); }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-700"/> Modifier le mot de passe
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">
              Définissez un nouveau mot de passe pour <strong>{u.prenom} {u.nom}</strong>.
              Il sera haché avec bcrypt et appliqué immédiatement.
            </p>
            <div>
              <Label>Nouveau mot de passe</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="Au moins 6 caractères"
                  data-testid="input-new-password"
                  autoComplete="new-password"
                />
                <Button type="button" variant="outline" size="icon" onClick={() => setShowNewPwd((v) => !v)}>
                  {showNewPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </Button>
              </div>
            </div>
            <div>
              <Label>Confirmer le mot de passe</Label>
              <Input
                type={showNewPwd ? 'text' : 'password'}
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Re-saisissez le mot de passe"
                data-testid="input-confirm-password"
                autoComplete="new-password"
                className="mt-1"
              />
              {confirmPwd && newPwd !== confirmPwd && (
                <p className="text-xs text-rose-600 mt-1">Les mots de passe ne correspondent pas.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPwd(false)} disabled={pwdSaving}>Annuler</Button>
            <Button
              onClick={submitNewPwd}
              disabled={pwdSaving || !newPwd || newPwd !== confirmPwd || newPwd.length < 6}
              className="bg-blue-700"
              data-testid="btn-submit-new-password"
            >
              {pwdSaving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin"/> : <Save className="w-4 h-4 mr-1"/>}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Bloquer le compte */}
      <Dialog open={showBlock} onOpenChange={(o) => { setShowBlock(o); if (!o) setBlockReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Ban className="w-5 h-5"/> Bloquer le compte
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Le compte de <strong>{u.prenom} {u.nom}</strong> sera bloqué.
              Le client ne pourra plus se connecter ni effectuer d'opérations.
            </p>
            <div>
              <Label>Motif du blocage <span className="text-rose-600">*</span></Label>
              <Textarea
                rows={4}
                placeholder="Précisez la raison du blocage (visible dans le journal d'audit)..."
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                data-testid="textarea-block-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlock(false)} disabled={blockSaving}>Annuler</Button>
            <Button
              variant="destructive"
              onClick={doBlock}
              disabled={blockSaving || !blockReason.trim()}
              data-testid="btn-block-confirm"
            >
              {blockSaving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin"/> : <Ban className="w-4 h-4 mr-1"/>}
              Bloquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Ajuster le solde (Feature v4) */}
      <Dialog
        open={showAdjustBal}
        onOpenChange={(o) => { setShowAdjustBal(o); if (!o) setAdjustBal({ balance: '', motif: '' }); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Wallet className="w-5 h-5"/> {t('admin.adjustBalance')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-sm">
              <div className="text-xs text-slate-500 uppercase font-medium">{t('admin.currentBalance')}</div>
              <div className="text-xl font-bold">{(u.solde || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
            </div>
            <div>
              <Label>{t('admin.newBalance')} (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={adjustBal.balance}
                onChange={(e) => setAdjustBal({ ...adjustBal, balance: e.target.value })}
                data-testid="input-adjust-balance-value"
                autoFocus
              />
            </div>
            <div>
              <Label>{t('admin.balanceMotif')}</Label>
              <Textarea
                rows={3}
                value={adjustBal.motif}
                onChange={(e) => setAdjustBal({ ...adjustBal, motif: e.target.value })}
                placeholder={t('admin.balanceMotifPlaceholder')}
                data-testid="input-adjust-balance-motif"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustBal(false)} disabled={adjustBalSaving}>Annuler</Button>
            <Button
              className="bg-blue-700"
              onClick={doAdjustBal}
              disabled={adjustBalSaving || !adjustBal.balance || !adjustBal.motif.trim()}
              data-testid="btn-adjust-balance-confirm"
            >
              {adjustBalSaving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin"/> : <Save className="w-4 h-4 mr-1"/>}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmation déblocage */}
      <Dialog open={showUnblockConfirm} onOpenChange={setShowUnblockConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShieldOff className="w-5 h-5"/> Débloquer le compte
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-slate-600 dark:text-slate-300">
            Êtes-vous sûr de vouloir débloquer le compte de <strong>{u.prenom} {u.nom}</strong> ?
            Le client pourra à nouveau se connecter et effectuer des opérations.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnblockConfirm(false)} disabled={unblockSaving}>Annuler</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={doUnblock}
              disabled={unblockSaving}
              data-testid="btn-unblock-confirm"
            >
              {unblockSaving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin"/> : <ShieldOff className="w-4 h-4 mr-1"/>}
              Débloquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
